// Schedule: the login's run days and window, and (newer API) each job's own days, saved as a
// console override on top of the desk's registry file from the next run day. Same limits the
// desk enforces: daytime window (06:00-19:00), job days only within the login's days, catch-up
// only with days, and one required Why for the audit trail. Overrides never expire; after 14
// days the dialog and the drawer ask for a PR that moves it into Desk/credentials.yaml.

import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { resetSchedule, setSchedule } from './api';
import { WEEK, daysText, driftNotice, jobLabel, scheduleChanges, scheduleDraft, scheduleOf, scheduleProblem } from './deskModel';
import { useTones } from './tones';

const cap = (d) => d[0].toUpperCase() + d.slice(1);

const DayToggles = ({ value, allowed, onChange, label }) => (
    <ToggleButtonGroup size="small" value={value} onChange={(e, v) => onChange(WEEK.filter((d) => v.includes(d)))}
        aria-label={label} sx={{ flexWrap: 'wrap' }}>
        {WEEK.map((d) => (
            <ToggleButton key={d} value={d} disabled={allowed ? !allowed.includes(d) : false}
                sx={{
                    textTransform: 'none', px: 1.25, py: 0.25, minWidth: 44,
                    // selected: filled primary with contrasting text, in both themes; unselected stays outlined
                    // dark mode: dark text on the lighter primary (white on it is only 3.2:1)
                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'primary.main', borderColor: 'primary.main',
                        color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.primary.contrastText) },
                    '&.Mui-disabled': { opacity: 0.45 },
                }}>
                {cap(d)}
            </ToggleButton>
        ))}
    </ToggleButtonGroup>
);

const ScheduleDialog = ({ login, onClose, onDone }) => {
    const t = useTones();
    const [draft, setDraft] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [warnings, setWarnings] = useState(null); // the save's warnings: the dialog stays open to show them
    useEffect(() => {
        if (!login) return;
        setDraft(scheduleDraft(login)); setReason(''); setError(null); setWarnings(null);
    }, [login]);
    if (!login || !draft) return <Dialog open={false} />;

    // Per-job days need the API that knows override_jobs; an older one would drop them silently.
    const withJobs = 'override_jobs' in login && (login.jobs || []).length > 0;
    const { changes, payload } = scheduleChanges(login, draft, withJobs);
    const problem = scheduleProblem(draft, reason, withJobs);
    const custom = scheduleOf(login).custom || Boolean(login.override_jobs && Object.keys(login.override_jobs).length);
    const drift = driftNotice(login);

    const setLoginDays = (days) => setDraft((d) => ({
        ...d, days,
        // a job can only run on the login's days
        jobs: Object.fromEntries(Object.entries(d.jobs).map(([jt, j]) => [jt, { ...j, days: j.inherit ? days : j.days.filter((x) => days.includes(x)) }])),
    }));
    const setJob = (jt, patch) => setDraft((d) => ({ ...d, jobs: { ...d.jobs, [jt]: { ...d.jobs[jt], ...patch } } }));
    const save = async (fn, msg) => {
        setBusy(true); setError(null);
        try {
            const res = await fn();
            const w = Array.isArray(res?.warnings) ? res.warnings.filter(Boolean).map(String) : [];
            if (w.length) setWarnings({ list: w, msg }); else onDone(msg);
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className="d-font">
                Schedule for {login.id}
                <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                    Applies from the next run day. Today&apos;s planned run keeps its time.
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>Login runs on</Typography>
                <DayToggles value={draft.days} onChange={setLoginDays} label="Login days" />
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', my: 1.5 }}>
                    <TextField label="Window start" type="time" size="small" value={draft.start}
                        onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))} />
                    <TextField label="Window end" type="time" size="small" value={draft.end}
                        onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))} />
                    <Typography className="d-font" sx={{ fontSize: 12, color: t.muted }}>
                        {login.timezone} · allowed 06:00–19:00. File: {login.window?.start}–{login.window?.end} {daysText(login.days || [])}.
                    </Typography>
                </Box>

                {withJobs && (
                    <Box sx={{ mb: 1.5 }}>
                        <Typography className="d-font" sx={{ fontSize: 12, color: t.muted, mb: 1 }}>
                            A job can only run on the login&apos;s days.
                        </Typography>
                        {(login.jobs || []).map((j) => {
                            const jd = draft.jobs[j.job_type] || { days: [], catch_up: null };
                            return (
                                <Box key={j.job_type} sx={{ mb: 1.25 }} data-job-days={j.job_type}>
                                    <Typography className="d-font" sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                                        {jobLabel(j, login)} runs on
                                    </Typography>
                                    <DayToggles value={jd.days} allowed={draft.days} label={`${jobLabel(j, login)} days`}
                                        onChange={(days) => setJob(j.job_type, { days, inherit: false })} />
                                    <FormControlLabel sx={{ mt: 0.25 }}
                                        control={<Checkbox size="small" checked={jd.catch_up === 'week'}
                                            onChange={(e) => setJob(j.job_type, { catch_up: e.target.checked ? 'week' : null })} />}
                                        label={<Typography className="d-font" sx={{ fontSize: 12 }}>Catch up later in the week if missed</Typography>} />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                <TextField label="Why (required)" fullWidth size="small" multiline minRows={2} value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. HMH publishes its weekly export on Mondays (catalog creationDate history)" />

                <Box data-change className="d-font" sx={{ fontSize: 13, p: 1.25, mt: 1.5, borderRadius: 1, bgcolor: t.dark ? 'action.hover' : '#f5f6f8' }}>
                    {!changes.length ? 'No change yet.' : (
                        <>
                            <b>Change:</b> {changes.join('; ')}, from the next run day.{' '}
                            {problem ? problem : 'Recorded with who and why. After 14 days the page asks for a PR to move it into the code file.'}
                        </>
                    )}
                </Box>
                {drift && <Alert severity="warning" sx={{ mt: 1.5 }}>{drift}</Alert>}
                {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
                {warnings && (
                    <Alert severity="warning" sx={{ mt: 1.5 }} data-warnings>
                        Saved, with {warnings.list.length === 1 ? 'a warning' : `${warnings.list.length} warnings`}:
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{warnings.list.map((w) => <li key={w}>{w}</li>)}</Box>
                    </Alert>
                )}
            </DialogContent>
            {warnings ? (
                <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
                    <Button variant="contained" onClick={() => onDone(warnings.msg)}>Close</Button>
                </DialogActions>
            ) : (
            <DialogActions sx={{ '& .MuiButton-root': { textTransform: 'none' } }}>
                {custom && (
                    <Button disabled={busy} onClick={() => save(() => resetSchedule(login.id), `${login.id}: back to the file's schedule`)}>
                        Reset to file
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={busy || !changes.length || Boolean(problem)}
                    onClick={() => save(() => setSchedule(login.id, { ...payload, reason: reason.trim() }), `${login.id}: schedule saved, from the next run day`)}>
                    Save
                </Button>
            </DialogActions>
            )}
        </Dialog>
    );
};

export default ScheduleDialog;
